import type { FgConfig } from '../parser/types'
import { SEVERITY_ORDER, type Finding, type Rule, type Severity } from './types'

export interface AuditResult {
  findings: Finding[]
  /** How many rules ran, so the UI can honestly say "0 of 18 checks failed". */
  rulesRun: number
  counts: Record<Severity, number>
  warnings: FgConfig['warnings']
}

/**
 * Runs every rule and returns findings ordered worst-first, then by rule id so the
 * output is deterministic — that stability is what makes golden-file tests possible
 * and what lets a user diff two audits of the same device.
 *
 * A rule that throws is contained rather than fatal: one bad rule must not deny the
 * user the other seventeen. The failure surfaces as an `info` finding so it is visible
 * instead of silently reducing coverage.
 */
export function audit(config: FgConfig, rules: Rule[]): AuditResult {
  const findings: Finding[] = []

  for (const rule of rules) {
    try {
      findings.push(...rule.run(config))
    } catch (error) {
      findings.push({
        ruleId: rule.id,
        severity: 'info',
        title: `Check "${rule.title}" could not complete`,
        detail:
          'This check hit an unexpected structure in the configuration and was skipped. ' +
          'Every other check still ran. ' +
          (error instanceof Error ? error.message : String(error)),
        remediation: 'No action required. Please report the configuration shape if you can share it.',
      })
    }
  }

  findings.sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      a.ruleId.localeCompare(b.ruleId) ||
      (a.location ?? '').localeCompare(b.location ?? ''),
  )

  const counts: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 }
  for (const f of findings) counts[f.severity]++

  return { findings, rulesRun: rules.length, counts, warnings: config.warnings }
}
